import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { createImageDefaults, fallbackImageSize } from './image-defaults.factory.js';

/**
 * Subscribes to `place/image` UiActions and materialises a new image
 * SYNCHRONOUSLY (PRD-F4 §G2 / Variante C — H6 Async-Race-Fix):
 *
 * - Builds the ImageObject via `createImageDefaults` with `naturalSize=null`
 *   (Fallback-Bbox path — square placeholder of `MAX_IMAGE_DIMENSION_PX /
 *   viewportScale`). The renderer corrects the bounding box to the natural
 *   aspect ratio once `<img>` decodes, without writing to history.
 * - Pushes an `AddObjectCommand` onto history immediately, then re-emits
 *   `selection/set` for the new id.
 *
 * MUST NOT await `<img>.onload`, use `setTimeout`, or otherwise defer the
 * dispatch to a microtask — that would re-introduce the H6 race where rapid
 * paste sequences arrive out of order.
 *
 * Lifecycle: subscription is bound to `bus.destroy$` via `takeUntil`. The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 *
 * Image objects have NO inline editor — the controller MUST NOT dispatch any
 * `edit/enter-*` action.
 */
export class ImagePlacementController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;

    constructor(bus: WhiteboardBus, state: BoardState, history: History<BoardState>) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/image' }> =>
                        a.type === 'place/image',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[ImagePlacementController] bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/image' }>): void {
        const scale = this.#bus.viewport$.value.scale;
        // The dispatcher decodes the bitmap and passes the aspect-correct,
        // viewport-fitted world `size`; only fall back to a square when it is
        // absent (e.g. the image failed to decode).
        const size = action.size ?? fallbackImageSize(scale);
        // An asset-backed `src` WINS over an inline `dataUrl` — the same
        // precedence the renderer applies (`obj.src ?? obj.dataUrl`). Stays
        // synchronous: no await, no setTimeout (see the H6 note above).
        const source = action.src ? { src: action.src } : { dataUrl: action.dataUrl ?? '' };
        const image = createImageDefaults({ x: action.worldX, y: action.worldY }, source, size);

        const cmd = new AddObjectCommand(image);
        this.#history.push(cmd, this.#state);

        const newId = objectId(image.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
    }
}
